import React, { useRef, useEffect } from 'react';
import { formatDate, formatBirthdayWithAge } from '@repo/common';
import {
  formatDescriptionToDots,
  getCareerLevelLabel,
  getLocationCountry,
  hasValue,
} from '@repo/common';

const PreviewContentModal = ({
  resumeData,
  pageHeight = 842,
  setTotalPages,
}) => {
  const {
    profile,
    visa,
    summary,
    education,
    experience,
    skill,
    activity,
    language,
    link,
  } = resumeData;
  const containerRef = useRef(null);
  const koreanName = Array.isArray(profile?.koreanName)
    ? profile.koreanName.join(' ')
    : typeof profile?.koreanName === 'string'
    ? profile.koreanName
    : '';

  const PAGE_PADDING_TOP = 24;
  const PAGE_PADDING_BOTTOM = 24;
  const effectivePageHeight =
    pageHeight - PAGE_PADDING_TOP - PAGE_PADDING_BOTTOM;

  useEffect(() => {
    if (!containerRef.current) return;
    const blocks = containerRef.current.querySelectorAll('.break-inside-avoid');

    blocks.forEach((block) => (block.style.marginTop = '0px'));

    blocks.forEach((block) => {
      const blockTop = block.offsetTop - PAGE_PADDING_TOP;
      const blockHeight = block.offsetHeight;

      const startPage = Math.floor(blockTop / effectivePageHeight);
      const endPage = Math.floor(
        (blockTop + blockHeight - 1) / effectivePageHeight
      );

      if (startPage !== endPage) {
        const nextPageStart = endPage * effectivePageHeight + PAGE_PADDING_TOP;
        const gap = nextPageStart - blockTop;
        block.style.marginTop = `${gap}px`;
      }
    });

    if (blocks.length > 0) {
      const lastBlock = blocks[blocks.length - 1];
      const lastBlockBottom = lastBlock.offsetTop + lastBlock.offsetHeight;
      const containerHeight = containerRef.current.scrollHeight;

      const remainingSpace =
        containerHeight - PAGE_PADDING_BOTTOM - lastBlockBottom;
      if (remainingSpace < PAGE_PADDING_BOTTOM) {
        lastBlock.style.marginBottom = `${
          PAGE_PADDING_BOTTOM - remainingSpace
        }px`;
      }
    }

    const finalContentHeight =
      containerRef.current.scrollHeight -
      PAGE_PADDING_TOP -
      PAGE_PADDING_BOTTOM;

    const calculatedPages = Math.ceil(finalContentHeight / effectivePageHeight);
    if (setTotalPages) setTotalPages(calculatedPages);

    if (setTotalPages) {
      setTotalPages(calculatedPages);
    }
  }, [resumeData, pageHeight, setTotalPages, effectivePageHeight]);

  return (
    <div
      className='flex flex-col bg-grayscale-gray00 relative px-[30px] py-6'
      style={{
        height: '842px',
        overflow: 'visible',
      }}
    >
      <div ref={containerRef}>
        <div>
          {/* Header */}
          {profile?.career && (
            <div className='flex justify-end pb-4'>
              <div className='relative w-fit text-right text-[14px] leading-[1.4] tracking-[0.22em] font-medium text-text-primary'>
                {Array.isArray(profile.targetRole) &&
                profile.targetRole.length > 0
                  ? `${profile.targetRole.join(', ')}, ${getCareerLevelLabel(
                      profile.career
                    )}`
                  : getCareerLevelLabel(profile.career)}
                <div className='absolute right-0 -left-[60px] top-full border-[1px] border-grayscale-gray10' />
              </div>
            </div>
          )}

          {/* LEFT */}
          <div className='flex h-full w-full'>
            {/* Profile */}
            <div className='flex-[6.5] pr-[23px]'>
              <div className='break-inside-avoid'>
                {profile && (
                  <div className='pb-2'>
                    {(koreanName || profile.lastName || profile.firstName) && (
                      <div>
                        <div className='typo-b14'>{koreanName}</div>
                        <div className='text-[18px] font-bold'>
                          {profile.firstName}, {profile.lastName}
                        </div>
                      </div>
                    )}

                    {(profile.location || profile.koreanSkills) && (
                      <div className='flex flex-wrap items-center content-start gap-1'>
                        {profile.location && (
                          <span className='px-1 py-0.5 bg-grayscale-gray01 text-[9px] rounded text-grayscale-gray06'>
                            Based in {getLocationCountry(profile.location)}
                          </span>
                        )}
                        {profile.koreanSkills && (
                          <span className='px-1 py-0.5 bg-grayscale-gray01 text-[9px] rounded text-grayscale-gray06'>
                            {profile.topik
                              ? profile.topik.level.split(' (')[0]
                              : profile.koreanSkills}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Profile */}
              {(hasValue(profile?.birthday) ||
                hasValue(profile?.nationality) ||
                hasValue(profile?.phone?.number) ||
                hasValue(profile?.email)) && (
                <div>
                  <div className='pb-[6px] break-inside-avoid'>
                    <div className='text-[14px] leading-[1.2] tracking-[0.01em] font-medium inline-block bg-[linear-gradient(transparent,transparent_50%,#1DC37B_50%)]'>
                      Profile
                    </div>
                  </div>
                  <div className='text-[10px] leading-[1.4] font-regular space-y-[2px] text-text-primary pb-[8px]'>
                    {profile.birthday && (
                      <p>{formatBirthdayWithAge(profile.birthday)}</p>
                    )}
                    {hasValue(profile?.nationality) && (
                      <p>
                        Nationality{' '}
                        {Array.isArray(profile.nationality)
                          ? profile.nationality.join(', ')
                          : profile.nationality}
                      </p>
                    )}
                    {Array.isArray(profile.phone) &&
                      profile.phone.length > 0 && (
                        <p>
                          Contact{' '}
                          {profile.phone
                            .map((p) => `${p.countryCode} ${p.number}`)
                            .join(', ')}
                        </p>
                      )}
                    {hasValue(profile?.email) && (
                      <p>
                        Email{' '}
                        {Array.isArray(profile.email)
                          ? profile.email.join(', ')
                          : profile.email}
                      </p>
                    )}
                  </div>
                </div>
              )}

              <div className='flex flex-col gap-4'>
                {/* Visa */}
                {visa?.length > 0 &&
                  visa.map((v, idx) => {
                    const isNoVisa = v.visaType?.startsWith('No');

                    return (
                      <div
                        key={idx}
                        className={`flex flex-col ${
                          isNoVisa ? 'gap-0' : 'gap-2'
                        }`}
                      >
                        <div
                          className={`w-fit flex items-center bg-grayscale-gray01 px-[16px]
                              ${isNoVisa ? 'py-[13px]' : 'py-[6px]'}
                              rounded-[8px] break-inside-avoid`}
                        >
                          <div className='flex flex-col items-center justify-center pr-3'>
                            <div
                              className='bg-green-deep02 text-green-light02 px-[10px] py-0.5 rounded-[6px]
                              flex items-center justify-center text-[10px] font-medium leading-[1.4]'
                            >
                              {isNoVisa
                                ? `${v.visaType} ${v.visaTypeText}`
                                : v.visaType}
                            </div>

                            {!isNoVisa && (
                              <span className='text-[9px] text-grayscale-gray06 mt-1 whitespace-nowrap'>
                                {v.visaTypeText} visa
                              </span>
                            )}
                          </div>

                          {!isNoVisa && v.expiryDate && (
                            <span className='flex items-center gap-2 ml-4'>
                              <span className='text-[9px] font-regular leading-[1.4] text-grayscale-gray06'>
                                Date of Expiry
                              </span>
                              <span className='text-[10px] font-medium leading-[1.4] text-text-primary'>
                                {formatDate(v.expiryDate)}
                              </span>
                            </span>
                          )}

                          {isNoVisa && (
                            <span className='flex items-center gap-2 ml-4'>
                              <span className='text-[9px] font-regular leading-[1.4] text-grayscale-gray06'>
                                No current Korean visa
                              </span>
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}

                {/* Summary */}
                {hasValue(summary) && (
                  <div className='break-inside-avoid'>
                    <div className='text-[14px] leading-[1.4] tracking-[0.22em] font-medium text-text-primary pb-1'>
                      SUMMARY
                    </div>
                    <div className='flex items-start pl-[10px] border-l-grayscale-gray10 border-l-[1px] border-box'>
                      <p className='text-[10px] leading-[1.4] tracking-[0] font-regular whitespace-pre-line break-words'>
                        {summary}
                      </p>
                    </div>
                  </div>
                )}

                {/* Experience */}
                {experience?.length > 0 && (
                  <section className='break-inside-avoid'>
                    <div className='text-[14px] leading-[1.4] tracking-[0.22em] font-medium text-text-primary pb-[8px] break-inside-avoid'>
                      WORK EXPERIENCE
                    </div>
                    <div className='flex flex-row items-center pb-1 gap-5'>
                      <div className='w-[76px] border-b-[1px] border-grayscale-gray10' />
                      <div className='flex-1 border-b-[1px] border-green-deep03' />
                    </div>
                    <div className='flex flex-col items-start p-0 gap-4 overflow-hidden'>
                      {experience.map((exp, idx) => (
                        <div
                          key={idx}
                          className='flex gap-5 w-full overflow-hidden '
                        >
                          <div className='flex-shrink-0 w-[76px] flex flex-col text-[10px] leading-[1.4] tracking-[0] text-text-primary gap-1'>
                            {exp.employmentPeriod && (
                              <div className='font-regular'>
                                {exp.employmentPeriod}
                              </div>
                            )}
                            {exp.company && (
                              <div className='font-medium'>{exp.company}</div>
                            )}
                            {exp.jobTitle && (
                              <div className='font-medium italic text-text-secondary'>
                                {exp.jobTitle}
                              </div>
                            )}
                            {exp.employmentType && (
                              <div className='font-regular'>
                                {exp.employmentType}
                              </div>
                            )}
                          </div>

                          <div className='flex flex-col flex-1 min-w-0 overflow-hidden gap-1'>
                            {exp.headline && (
                              <div className='w-fit text-[10px] leading-[1.4] tracking-[0] font-medium text-text-primary inline-block bg-[linear-gradient(transparent,transparent_40%,#4DD5A3_40%)]'>
                                {exp.headline}
                              </div>
                            )}

                            {exp.keyAchievements?.length > 0 && (
                              <div className='list-disc list-inside break-words text-text-primary'>
                                {exp.keyAchievements.map((ach, achIdx) => (
                                  <div key={achIdx} className='mb-1'>
                                    {ach.title && (
                                      <div className='text-[8.5px] leading-[1.4] tracking-[-0.03em] font-medium'>
                                        {ach.title}
                                      </div>
                                    )}

                                    {ach.description && (
                                      <ul className='list-none p-0 m-0'>
                                        {formatDescriptionToDots(
                                          ach.description
                                        ).map((line, idx) => (
                                          <li
                                            key={idx}
                                            className='flex items-start text-[8.5px] leading-[1.4] tracking-[-0.03em] font-regular'
                                          >
                                            <span className='mr-[3px] flex-shrink-0'>
                                              •
                                            </span>
                                            <span className='flex-1 min-w-0 break-words'>
                                              {line}
                                            </span>
                                          </li>
                                        ))}
                                      </ul>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </section>
                )}

                {/* Activity */}
                {activity?.length > 0 && (
                  <section className='break-inside-avoid'>
                    <div className='text-[14px] leading-[1.4] tracking-[0.22em] font-medium text-text-primary pb-2 break-inside-avoid'>
                      ACTIVITIES
                    </div>
                    <div className='flex flex-row items-center pb-1 gap-5'>
                      <div className='w-[76px] border-b-[1px] border-grayscale-gray10' />
                      <div className='flex-1 border-b-[1px] border-green-deep03' />
                    </div>
                    <div className='flex flex-col items-start p-0 gap-4 overflow-hidden'>
                      {activity.map((act, idx) => (
                        <div
                          key={idx}
                          className='flex gap-5 w-full overflow-hidden break-inside-avoid'
                        >
                          <div className='flex-shrink-0 w-[76px] flex flex-col text-[10px] leading-[1.4] tracking-[0] text-text-primary'>
                            <div className='font-regular'>{act.date}</div>
                          </div>
                          <div className='flex flex-col flex-1 min-w-0 overflow-hidden text-[10px] leading-[1.4] tracking-[0] text-text-primary'>
                            <div className='font-medium truncate'>
                              {act.title}
                            </div>
                            <div className='font-regular overflow-hidden'>
                              <ul className='list-none'>
                                {formatDescriptionToDots(act.description).map(
                                  (line, idx) => (
                                    <li
                                      key={idx}
                                      className='flex items-start text-[10px] leading-[1.4] tracking-[0] font-regular'
                                    >
                                      <span className='mr-[1px] flex-shrink-0'>
                                        •
                                      </span>
                                      <span className='flex-1 break-words'>
                                        {line}
                                      </span>
                                    </li>
                                  )
                                )}
                              </ul>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </section>
                )}
              </div>
            </div>

            {/* RIGHT */}
            <div className='flex-[3.5] min-w-0'>
              {/* Profile Image */}
              <div className='pb-[28px]'>
                {profile?.profileUrl && (
                  <div className='w-[180px] h-[220px] bg-grayscale-gray02 overflow-hidden'>
                    <img
                      src={profile.profileUrl}
                      alt='profile'
                      className='w-full h-full object-cover'
                    />
                  </div>
                )}
              </div>

              <div className='flex flex-col gap-4'>
                {/* Education */}
                {education?.length > 0 && (
                  <section>
                    <div className='text-[14px] leading-[1.4] tracking-[0.22em] font-medium text-text-primary pb-1'>
                      EDUCATION
                    </div>
                    <div className='flex flex-col gap-3'>
                      {education.map((edu) => (
                        <div key={edu.id} className='break-inside-avoid'>
                          <div className='flex flex-col gap-1 text-[10px] leading-[1.4] tracking-[0] font-regular text-text-primary'>
                            <div>
                              {edu.degree}, {edu.fieldOfStudy}
                            </div>
                            <div>{edu.schoolName}</div>
                            <div>{edu.studyPeriod}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </section>
                )}

                {/* Skills / Languages */}
                {(language?.length > 0 || skill?.length > 0) && (
                  <section>
                    <div className='text-[14px] leading-[1.4] tracking-[0.22em] font-medium text-text-primary pb-1'>
                      SKILLS
                    </div>
                    <div className='flex flex-col gap-4'>
                      {language?.length > 0 && (
                        <div className='flex flex-col gap-2'>
                          <div className='text-[10px] leading-[1.4] tracking-[0] font-regular text-text-primary'>
                            • Languages
                          </div>
                          {language.map((lang) => (
                            <div
                              key={lang.id}
                              className='flex text-[10px] leading-[1.4] tracking-[0] font-regular break-inside-avoid'
                            >
                              <div className='flex gap-2'>
                                <div className='min-w-[50px] text-text-primary flex-1'>
                                  {lang.language}
                                </div>
                                <div className='flex flex-col gap-1'>
                                  <span className='text-text-primary'>
                                    {lang.proficiencyLevel}
                                  </span>
                                  <span className='text-text-secondary flex gap-2'>
                                    {lang.test} {lang.score}
                                    <span className='text-grayscale-gray06'>
                                      {formatDate(lang.issueDate, {
                                        yearMonthOnly: true,
                                      })}
                                    </span>
                                  </span>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      {skill?.length > 0 && (
                        <div className='flex flex-col gap-2 text-[10px] leading-[1.4] tracking-[0] font-regular text-text-primary'>
                          <div>• Technical</div>
                          <div className='flex flex-col gap-1'>
                            {skill.map((tech, idx) => (
                              <div
                                key={`${tech}-${idx}`}
                                className='break-inside-avoid'
                              >
                                {tech}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </section>
                )}

                {/* Links */}
                {link?.length > 0 && (
                  <section>
                    <div className='text-[14px] leading-[1.4] tracking-[0.22em] font-medium text-text-primary pb-1'>
                      LINKS
                    </div>
                    <div>
                      {link.map((l) => (
                        <div
                          key={l.id}
                          href={l.link}
                          target='_blank'
                          rel='noreferrer'
                          className='block text-[10px] break-words break-inside-avoid'
                        >
                          <span className='mr-[3px] flex-shrink-0'>•</span>
                          <span className='break-words flex-1 min-w-0'>
                            {l.link.replace('https://', '')}
                          </span>
                        </div>
                      ))}
                    </div>
                  </section>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PreviewContentModal;
